import { Injectable } from '@angular/core';
import { interval, Observable, Subject } from 'rxjs';
import { debounce, filter } from 'rxjs/operators';
import { IWidgetSettingsModel } from '@fi-shared/widget/widget-settings.model';
import { IParallelTasksData, parallelTasksQueue } from '@fi-shared/utils/parallel-tasks-queue';
import { IDocumentListingItem } from '@fi-shared/widget/document-listing/document-listing-base';
import { ObjectTagFilterParameter } from '@fi-shared/models';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';

export interface IWidgetSettingsUpdated {
    settingsItem: IWidgetSettingsModel;
    skipReloadData?: boolean;
}

const NUMBER_OF_SIMULTANEOUS_REQUESTS = 4;

@Injectable()
export class WidgetSharedService {
    private widgetSettingsChanged$ = new Subject<IWidgetSettingsUpdated>();
    private requestsQueue: IParallelTasksData;

    constructor(private fiBackend: FiBackendService) {
        this.requestsQueue = parallelTasksQueue(NUMBER_OF_SIMULTANEOUS_REQUESTS);

        this.initObjectTagFilterParameterQueue();
    }

    raiseWidgetSettingsChanged(settingsItem: IWidgetSettingsModel, data: { skipReloadData?: boolean }) {
        if (!data) {
            data = {};
        }
        this.widgetSettingsChanged$.next({
            settingsItem: settingsItem,
            skipReloadData: data.skipReloadData,
        });
    }

    getWidgetSettingsChanged$(settingsItemFn: () => IWidgetSettingsModel): Observable<IWidgetSettingsUpdated> {
        return this.widgetSettingsChanged$.pipe(filter((v) => v.settingsItem == settingsItemFn()));
    }

    addRequestToQueue(requestFn: () => Promise<void>) {
        this.requestsQueue.add(requestFn);
    }

    //#region GetItemsByTagFilter;

    private objectTagFilterParameters: ObjectTagFilterParameter[] = [];
    private onNewItemsByTagFilterRequest$ = new Subject<void>();
    private itemsByTagFilterResult$ = new Subject<{ guid: string; data: IDocumentListingItem[]; isError?: boolean }>();
    private initObjectTagFilterParameterQueue() {
        this.onNewItemsByTagFilterRequest$.pipe(debounce(() => interval(100))).subscribe(() => {
            const currentObjectTagFilterParameters = this.objectTagFilterParameters.slice();

            this.fiBackend
                .fetchDataPost<IGetItemsByTagFilterResult[]>(
                    'Widget/Widget/GetItemsByTagFilterBatch',
                    currentObjectTagFilterParameters,
                )
                .then((result) => {
                    result.forEach((resultItem) => {
                        this.itemsByTagFilterResult$.next({
                            guid: resultItem.Guid,
                            data: resultItem.Data,
                        });
                    });
                })
                .catch((err) => {
                    currentObjectTagFilterParameters.forEach((objectTagFilterParameter) => {
                        this.itemsByTagFilterResult$.next({
                            guid: objectTagFilterParameter.Guid,
                            data: undefined,
                            isError: true,
                        });
                    });
                });

            this.objectTagFilterParameters = [];
        });
    }
    getItemsByTagFilter(tagFilterParameter: ObjectTagFilterParameter): Promise<IDocumentListingItem[]> {
        // we should group simultaneous requests from 'page and file listing widgets' and make one batch request to backend
        this.objectTagFilterParameters.push(tagFilterParameter);
        this.onNewItemsByTagFilterRequest$.next();

        return new Promise<IDocumentListingItem[]>((resolve, reject) => {
            const sub = this.itemsByTagFilterResult$.subscribe((result) => {
                if (result.guid == tagFilterParameter.Guid) {
                    sub.unsubscribe();
                    if (!result.isError) {
                        resolve(result.data);
                    } else {
                        reject();
                    }
                }
            });
        });

        //return this.fiBackend.fetchDataPost<IDocumentListingItem[]>("Widget/Widget/GetItemsByTagFilter", tagFilterParameter);
    }

    //#endregion
}

interface IGetItemsByTagFilterResult {
    Guid: string;
    Data: IDocumentListingItem[];
}
